import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert } from 'react-bootstrap';
import { Formik, Field, FieldArray } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import Select from 'react-select';
import { adminInstance } from '../../services/axiosInstance';
import imageCompression from 'browser-image-compression';
import LoaderOverlay from '../../Components/shared/Loader';

// Service Type Options
const SERVICE_TYPES = [
    { value: 'demo', label: 'Demo Request', status: 'DEMO' },
    { value: 'warranty', label: 'Warranty Request', status: 'WARRANTY' },
    { value: 'service', label: 'Service Invoice Request', status: 'SERVICE' }
];

// Validation Schemas
const demoRequestSchema = Yup.object().shape({
    CUS_CODE: Yup.object().required('Customer is required'),
    CUS_ADDRESS: Yup.string().required('Address is required'),
    ITEM_CODE: Yup.object().required('Product is required'),
    //MODEL: Yup.string().required('Model is required'),
    //NOTE: Yup.string().required('Nature of complaint is required'),
    MS_SERVICE_REQUEST_ITEM: Yup.array().of(
        Yup.object().shape({
            ITEM_CODE: Yup.string().required('Item code is required'),
            ITEM_DESC: Yup.string().required('Description is required'),
            QTY: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
            UNIT_PRICE: Yup.number().min(0, 'Price cannot be negative')
        })
    )
});

const warrantyRequestSchema = Yup.object().shape({
    CUS_CODE: Yup.object().required('Customer is required'),
    CUS_ADDRESS: Yup.string().required('Address is required'),
    ITEM_CODE: Yup.object().required('Product is required'),
    //SERIAL_NO: Yup.string().required('Serial number is required'),
    //MODEL: Yup.string().required('Model is required'),
    INSTALLATION_DATE: Yup.date().required('Installation date is required'),
    //NOTE: Yup.string().required('Nature of complaint is required'),
    MS_SERVICE_REQUEST_ITEM: Yup.array().of(
        Yup.object().shape({
            ITEM_CODE: Yup.string().required('Item code is required'),
            ITEM_DESC: Yup.string().required('Description is required'),
            QTY: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
            UNIT_PRICE: Yup.number().min(0, 'Price cannot be negative')
        })
    )
});

const serviceInvoiceSchema = Yup.object().shape({
    CUS_CODE: Yup.object().required('Customer is required'),
    CUS_ADDRESS: Yup.string().required('Address is required'),
    ITEM_CODE: Yup.object().required('Product is required'),
    //MODEL: Yup.string().required('Model is required'),
    //PO_REFERENCE: Yup.string().required('PO/Email Reference is required'),
    //INSTALLATION_DATE: Yup.date().required('Installation date is required'),
    //NOTE: Yup.string().required('Nature of work done is required'),
});

// Enhanced Add Item Modal Component
const AddItemModal = ({ show, onHide, products, onAddItem }) => {
    const [selectedSpare, setSelectedSpare] = useState(null);
    const [QTY, setQTY] = useState(1);

    const handleAdd = () => {
        if (selectedSpare && QTY > 0) {
            onAddItem({
                ITEM_CODE: selectedSpare.ItemCode,
                ITEM_DESC: selectedSpare.ItemName,
                QTY: QTY,
                UNIT_PRICE: selectedSpare.UNIT_PRICE || 0
            });
            setSelectedSpare(null);
            setQTY(1);
            onHide();
        }
    };

    // Filter spares from selected product
    const spareOptions = products?.map(spare => ({
        value: spare.ItemCode,
        label: `${spare.ItemCode} - ${spare.ItemName}`,
        ...spare
    })) || [];

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Add Spare Item</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Search Spare Item</Form.Label>
                        <Select
                            options={spareOptions}
                            value={spareOptions.find(opt => opt.ItemCode === selectedSpare?.ITEM_CODE)}
                            onChange={(option) => setSelectedSpare(option)}
                            placeholder="Type to search spare items..."
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </Form.Group>

                    {selectedSpare && (
                        <Card className="mb-3 border-primary">
                            <Card.Header className="bg-primary text-white">
                                Selected Item Details
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <div className="mb-2">
                                            <strong>Item Code:</strong>
                                            <div className="text-muted">{selectedSpare.ItemCode}</div>
                                        </div>
                                    </Col>
                                    <Col md={8}>
                                        <div className="mb-2">
                                            <strong>Description:</strong>
                                            <div className="text-muted">{selectedSpare.ItemName}</div>
                                        </div>
                                    </Col>
                                </Row>
                                {selectedSpare.UNIT_PRICE && (
                                    <Row>
                                        <Col md={4}>
                                            <div className="mb-2">
                                                <strong>Unit Price:</strong>
                                                <div className="text-muted">₹{selectedSpare.UNIT_PRICE.toFixed(2)}</div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            value={QTY}
                            onChange={(e) => setQTY(Math.max(1, parseInt(e.target.value) || 1))}
                            style={{ maxWidth: '150px' }}
                        />
                        {selectedSpare && selectedSpare.UNIT_PRICE && (
                            <Form.Text className="text-muted">
                                Total: ₹{(selectedSpare.UNIT_PRICE * QTY).toFixed(2)}
                            </Form.Text>
                        )}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleAdd}
                    disabled={!selectedSpare}
                >
                    Add to List
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Main Component
const NewServiceRequestPage = () => {
    const [masterData, setMasterData] = useState({ customers: [], products: [] });
    const [loading, setLoading] = useState(true);
    const [serviceType, setServiceType] = useState('demo');
    const [showItemModal, setShowItemModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [files, setFiles] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const loadMasterData = async () => {
            try {
                const response = await adminInstance.get(`resource/GetResource/ServiceRequest`);
                debugger;
                setMasterData(response.data);
            } catch (error) {
                console.error('Error loading master data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadMasterData();
    }, []);

    // Prepare dropdown options
    const customerOptions = masterData?.customers?.map(customer => ({
        value: customer.CusCode,
        label: `${customer.CusCode} - ${customer.CusName}`,
        ...customer
    }));

    const productOptions = masterData?.products?.map(product => ({
        value: product.ItemCode,
        label: `${product.ItemCode} - ${product.ItemName}`,
        ...product
    }));

    const handleProductChange = (option, setFieldValue) => {
        setCurrentProduct(option);
        setFieldValue('ITEM_CODE', option);
        setFieldValue('MODEL', ''); // Reset model when product changes
    };

    const getValidationSchema = () => {
        switch (serviceType) {
            case 'demo':
                return demoRequestSchema;
            case 'warranty':
                return warrantyRequestSchema;
            case 'service':
                return serviceInvoiceSchema;
            default:
                return demoRequestSchema;
        }
    };

    const getInitialValues = () => {
        const baseValues = {
            CUS_CODE: null,
            CUS_ADDRESS: '',
            ITEM_CODE: null,
            MODEL: '',
            MS_SERVICE_REQUEST_ITEM: []
        };

        switch (serviceType) {
            case 'demo':
                return { ...baseValues, NOTE: '' };
            case 'warranty':
                return {
                    ...baseValues,
                    SERIAL_NO: '',
                    INSTALLATION_DATE: '',
                    NOTE: ''
                };
            case 'service':
                return {
                    ...baseValues,
                    PO_REFERENCE: '',
                    INSTALLATION_DATE: '',
                    NOTE: ''
                };
            default:
                return baseValues;
        }
    };

    const transformDataForAPI = (values, serviceType) => {
        const postData = {
            Request: {}, Items: []
        };
        debugger;
        const requestData = {
            REQ_TYPE: SERVICE_TYPES.find(st => st.value === serviceType).status,            
            CUS_CODE: values.CUS_CODE?.value || '',
            ITEM_CODE: values.ITEM_CODE?.value || '',
            MODEL: values.MODEL,
            NOTE: values.NOTE
        };

        // Add service type specific fields
        if (serviceType === 'warranty') {
            requestData.SERIAL_NO = values.SERIAL_NO;
            requestData.INSTALLATION_DATE = values.INSTALLATION_DATE;
        } else if (serviceType === 'service') {
            requestData.INSTALLATION_DATE = values.INSTALLATION_DATE;
            requestData.PO_REFERENCE = values.PO_REFERENCE;
        }

        postData.Request = requestData;

        // Transform items for API
        if (values.MS_SERVICE_REQUEST_ITEM && values.MS_SERVICE_REQUEST_ITEM.length > 0) {
            postData.Items = values.MS_SERVICE_REQUEST_ITEM.map(item => ({
                ITEM_CODE: item.ITEM_CODE,
                UNIT_PRICE: item.UNIT_PRICE || 0,
                QTY: item.QTY,
                AMOUNT: (item.UNIT_PRICE || 0) * item.QTY
            }));
        }

        return postData;
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const apiData = transformDataForAPI(values, serviceType);
            console.log('Submitting to API:', apiData);

            // Here you would make the actual API call
            // Example: await axios.post('/api/service-request', apiData);
            var compressedFiles = await Promise.all(
                files.map(async (file) => await compressImage(file))
            );
            const formData = new FormData();
            formData.append('data', JSON.stringify(apiData));
            compressedFiles.forEach(file => formData.append('files', file));
            setLoading(true);
            try {
                await adminInstance.post(`/servicerequest/save`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setFiles([]);
                alert('Request Saved successfully');
                navigate('/MyServiceRequest');
            } catch (err) {
                alert('Upload failed. Please try again.');
            }
            finally {
                setLoading(false);
            }

            //alert(`${SERVICE_TYPES.find(st => st.value === serviceType).label} submitted successfully!\nRequest Code: ${apiData.REQ_CODE}`);

            // Reset form
            resetForm();
            setSubmitting(false);
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Error submitting request. Please try again.');
            setSubmitting(false);
        }
    };

    // image compression
    const compressImage = async (file) => {
        const options = {
            maxSizeMB: 0.3,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        }
        try {
            const compressedFile = await imageCompression(file, options);
            console.log('compressedFile instanceof Blob', compressedFile instanceof Blob); // true
            console.log(`compressedFile size ${compressedFile.size / 1024 / 1024} MB`); // smaller than maxSizeMB

            // Convert Blob to File
            const fileName = file.name;
            const fileType = file.type;
            const compressedFileAsFile = new File([compressedFile], fileName, { type: fileType });

            return compressedFileAsFile; // Return as File instead of Blob
        } catch (error) {
            console.log(error);
        }
    }

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);        
        setFiles(selectedFiles);
    };

    //if (loading) {
    //    return (
    //        <Container className="mt-4 text-center">
    //            <div className="spinner-border text-primary" role="status">
    //                <span className="visually-hidden">Loading...</span>
    //            </div>
    //            <p className="mt-2">Loading ...</p>
    //        </Container>
    //    );
    //}

    return (
        <div className="card aon-card">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h4>
                        New Service Request
                    </h4>
                    <Button
                        variant="primary"
                        onClick={() => navigate('/MyServiceRequest')}
                    >
                        Back to list
                    </Button>
                </div>
            </div>
            <div className="card-body aon-card-body">
        <Container fluid className="py-4">
            <Card className="mb-3">
                {/*<Card.Header className="bg-primary text-white">*/}
                {/*    <h4 className="mb-0">Create Service Request</h4>*/}
                {/*</Card.Header>*/}
                <Card.Body>                    
                    <Row className="mb-4">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Select Request Type</Form.Label>
                                <Select
                                    options={SERVICE_TYPES}
                                    value={SERVICE_TYPES.find(st => st.value === serviceType)}
                                    onChange={(option) => {
                                        setServiceType(option.value);
                                        setCurrentProduct(null);
                                    }}
                                    placeholder="Select request type..."
                                    isSearchable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-end">
                            <Alert variant="info" className="mb-0 w-100">
                                <strong>Note:</strong> {SERVICE_TYPES.find(st => st.value === serviceType).label}
                            </Alert>
                        </Col>
                    </Row>

                    <Formik
                        key={serviceType} // Reinitialize form when service type changes
                        initialValues={getInitialValues()}
                        validationSchema={getValidationSchema()}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue }) => (
                            <Form onSubmit={handleSubmit}>
                                {/* Common Fields */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Customer *</Form.Label>
                                            <Select
                                                options={customerOptions}
                                                value={values.CUS_CODE}
                                                onChange={(option) => {
                                                    setFieldValue('CUS_CODE', option);
                                                    setFieldValue('CUS_ADDRESS', option?.CusAddress || '');
                                                }}
                                                placeholder="Select customer..."
                                                isSearchable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                            {errors.CUS_CODE && touched.CUS_CODE && (
                                                <Form.Text className="text-danger">{errors.CUS_CODE}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Address *</Form.Label>
                                            <Field
                                                as={Form.Control}
                                                name="CUS_ADDRESS"
                                                placeholder="Address will auto-fill from customer"
                                                readOnly
                                                className="bg-light"
                                            />
                                            {errors.CUS_ADDRESS && touched.CUS_ADDRESS && (
                                                <Form.Text className="text-danger">{errors.CUS_ADDRESS}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Product *</Form.Label>
                                            <Select
                                                options={productOptions}
                                                value={values.ITEM_CODE}
                                                onChange={(option) => handleProductChange(option, setFieldValue)}
                                                placeholder="Select product..."
                                                isSearchable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                            {errors.ITEM_CODE && touched.ITEM_CODE && (
                                                <Form.Text className="text-danger">{errors.ITEM_CODE}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Model *</Form.Label>
                                            <Field
                                                as={Form.Control}
                                                name="MODEL"
                                                placeholder="Enter model number"
                                            />
                                            {errors.MODEL && touched.MODEL && (
                                                <Form.Text className="text-danger">{errors.MODEL}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Service Type Specific Fields */}
                                {serviceType === 'demo' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nature of Complaint *</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            name="NOTE"
                                            rows={2}
                                            placeholder="Please describe in detail if any notes..."
                                            onChange={(e) => {
                                                setFieldValue('NOTE', e.target.value);
                                            }}
                                        />
                                        {errors.NOTE && touched.NOTE && (
                                            <Form.Text className="text-danger">{errors.NOTE}</Form.Text>
                                        )}
                                    </Form.Group>
                                )}

                                {serviceType === 'warranty' && (
                                    <>
                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Serial Number *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        name="SERIAL_NO"
                                                        placeholder="Enter serial number"
                                                    />
                                                    {errors.SERIAL_NO && touched.SERIAL_NO && (
                                                        <Form.Text className="text-danger">{errors.SERIAL_NO}</Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Installation Date *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="date"
                                                        name="INSTALLATION_DATE"
                                                    />
                                                    {errors.INSTALLATION_DATE && touched.INSTALLATION_DATE && (
                                                        <Form.Text className="text-danger">{errors.INSTALLATION_DATE}</Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Upload Files</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="file"
                                                        name="REQ_FILES"
                                                        multiple
                                                        accept="image/*"
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Photos
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nature of Complaint *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="NOTE"
                                                rows={2}
                                                placeholder="Please describe in detail if any notes..."
                                            />
                                            {errors.NOTE && touched.NOTE && (
                                                <Form.Text className="text-danger">{errors.NOTE}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </>
                                )}

                                {serviceType === 'service' && (
                                    <>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>PO/Email Approval Reference *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        name="PO_REFERENCE"
                                                        placeholder="Enter reference number"
                                                    />
                                                    {errors.PO_REFERENCE && touched.PO_REFERENCE && (
                                                        <Form.Text className="text-danger">{errors.PO_REFERENCE}</Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Installation Date *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="date"
                                                        name="INSTALLATION_DATE"
                                                    />
                                                    {errors.INSTALLATION_DATE && touched.INSTALLATION_DATE && (
                                                        <Form.Text className="text-danger">{errors.INSTALLATION_DATE}</Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nature of Work Done *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="NOTE"
                                                rows={2}
                                                placeholder="Please describe work completed..."
                                            />
                                            {errors.NOTE && touched.NOTE && (
                                                <Form.Text className="text-danger">{errors.NOTE}</Form.Text>
                                            )}
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Upload Service Report</Form.Label>
                                            <Field
                                                as={Form.Control}
                                                type="file"
                                                name="REQ_FILES"
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                {/* Spare Items Section (for Demo and Warranty) */}
                                {(serviceType === 'demo' || serviceType === 'warranty') && (
                                    <div className="mb-4">
                                        <Card>
                                            <Card.Header className="bg-light">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0">Spare Items Required</h5>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => setShowItemModal(true)}
                                                        disabled={!values.ITEM_CODE}
                                                    >
                                                        <i className="bi bi-plus-circle me-1"></i>
                                                        Add Spare Item
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body>
                                                <AddItemModal
                                                    show={showItemModal}
                                                    onHide={() => setShowItemModal(false)}
                                                    products={masterData.products}
                                                    onAddItem={(item) => {
                                                        const newItems = [...values.MS_SERVICE_REQUEST_ITEM, item];
                                                        setFieldValue('MS_SERVICE_REQUEST_ITEM', newItems);
                                                    }}
                                                />

                                                <FieldArray name="MS_SERVICE_REQUEST_ITEM">
                                                    {() => (
                                                        <div className="table-responsive">
                                                            {values.MS_SERVICE_REQUEST_ITEM.length === 0 ? (
                                                                <div className="text-center text-muted py-4">
                                                                    <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                                                                    <p className="mt-2">No spare items added yet</p>
                                                                    <Button
                                                                        variant="link"
                                                                        onClick={() => setShowItemModal(true)}
                                                                        disabled={!values.ITEM_CODE}
                                                                    >
                                                                        Click here to add items
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <Table striped bordered hover responsive="sm">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th width="5%">SL No</th>
                                                                            <th width="15%">Item Code</th>
                                                                            <th width="35%">Description</th>
                                                                            <th width="15%">Unit Price</th>
                                                                            <th width="15%">Quantity</th>
                                                                            <th width="15%">Amount</th>
                                                                            <th width="10%">Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {values.MS_SERVICE_REQUEST_ITEM.map((item, index) => (
                                                                            <tr key={index}>
                                                                                <td className="text-center">{index + 1}</td>
                                                                                <td>{item.ITEM_CODE}</td>
                                                                                <td>{item.ITEM_DESC}</td>
                                                                                <td className="text-end">₹{item.UNIT_PRICE?.toFixed(2) || '0.00'}</td>
                                                                                <td>
                                                                                    <Form.Control
                                                                                        type="number"
                                                                                        min="1"
                                                                                        value={item.QTY}
                                                                                        onChange={(e) => {
                                                                                            const newItems = [...values.MS_SERVICE_REQUEST_ITEM];
                                                                                            newItems[index].QTY = Math.max(1, parseInt(e.target.value) || 1);
                                                                                            setFieldValue('MS_SERVICE_REQUEST_ITEM', newItems);
                                                                                        }}
                                                                                        className="text-center"
                                                                                        style={{ minWidth: '80px' }}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-end">
                                                                                    ₹{((item.UNIT_PRICE || 0) * item.QTY).toFixed(2)}
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <Button
                                                                                        variant="outline-danger"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            const newItems = values.MS_SERVICE_REQUEST_ITEM.filter((_, i) => i !== index);
                                                                                            setFieldValue('MS_SERVICE_REQUEST_ITEM', newItems);
                                                                                        }}
                                                                                        title="Remove item"
                                                                                    >
                                                                                        <i className="bi bi-trash"></i>
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    {values.MS_SERVICE_REQUEST_ITEM.length > 0 && (
                                                                        <tfoot>
                                                                            <tr className="table-active">
                                                                                <td colSpan={5} className="text-end fw-bold">Total Amount:</td>
                                                                                <td className="text-end fw-bold">
                                                                                ₹{values.MS_SERVICE_REQUEST_ITEM.reduce((sum, item) =>
                                                                                        sum + ((item.UNIT_PRICE || 0) * item.QTY), 0
                                                                                    ).toFixed(2)}
                                                                                </td>
                                                                                <td></td>
                                                                            </tr>
                                                                        </tfoot>
                                                                    )}
                                                                </Table>
                                                            )}
                                                        </div>
                                                    )}
                                                </FieldArray>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                            // Clear all fields
                                            Object.keys(getInitialValues()).forEach(key => {
                                                setFieldValue(key, getInitialValues()[key]);
                                            });
                                            setCurrentProduct(null);
                                        }}
                                    >
                                        Clear Form
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                        size="lg"
                                    >
                                        {isSubmitting ? 'Submitting...' : `Submit ${SERVICE_TYPES.find(st => st.value === serviceType).label}`}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>
                </Container>
            </div>
        </div>
    );
};

export default NewServiceRequestPage;